import { buildApiUrl, getApiBaseCandidates } from "@/lib/apiBase";

export type ListingRecord = Record<string, string>;

export interface ListingListFilters {
  type?: "equipment" | "service";
  owner?: string;
  status?: "draft" | "pending" | "approved" | "rejected";
  q?: string;
  page?: number;
  limit?: number;
}

let preferredListingsApiBase = "";

const readJson = async <T>(response: Response): Promise<T> => {
  let payload: unknown;

  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const errorMessage = typeof payload === "object" && payload && "error" in payload
      ? String((payload as { error?: unknown }).error || "Listing request failed.")
      : "Listing request failed.";
    throw new Error(errorMessage);
  }

  return payload as T;
};

export const createListingRecord = async (token: string, listing: ListingRecord) => {
  const bases = getApiBaseCandidates(preferredListingsApiBase);
  let lastError: Error | null = null;

  for (const base of bases) {
    try {
      const response = await fetch(buildApiUrl("/api/listings", base), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ listing }),
      });

      const payload = await readJson<{ success: boolean; listing: ListingRecord }>(response);
      preferredListingsApiBase = base;
      return payload;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Listing request failed.");
    }
  }

  throw lastError || new Error("Listing request failed.");
};

const buildListingQuery = (filters: ListingListFilters = {}) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });

  params.set("t", String(Date.now()));

  const query = params.toString();
  return query ? `?${query}` : "";
};

export const fetchListingRecords = async (filters: ListingListFilters = {}) => {
  const bases = getApiBaseCandidates(preferredListingsApiBase);
  let lastError: Error | null = null;

  for (const base of bases) {
    try {
      const response = await fetch(buildApiUrl(`/api/listings${buildListingQuery(filters)}`, base), {
        method: "GET",
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      const payload = await readJson<{ success: boolean; listings: ListingRecord[]; meta?: { total?: number } }>(response);
      preferredListingsApiBase = base;
      return payload;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Listing request failed.");
    }
  }

  throw lastError || new Error("Listing request failed.");
};

export const fetchListingRecordById = async ({
  id,
  owner,
  type,
  limit = 200,
}: {
  id: string;
  owner?: string;
  type?: "equipment" | "service";
  limit?: number;
}) => {
  const normalizedId = String(id || "").trim();
  if (!normalizedId) return null;

  const response = await fetchListingRecords({ owner, type, limit });
  const listings = Array.isArray(response.listings) ? response.listings : [];

  return listings.find((item) => String(item.id || "").trim() === normalizedId) || null;
};

export const moderateListingRecord = async (
  token: string,
  listingId: string,
  payload: { status: "approved" | "rejected" | "pending"; adminDecisionNote?: string },
) => {
  const bases = getApiBaseCandidates(preferredListingsApiBase);
  let lastError: Error | null = null;

  for (const base of bases) {
    try {
      const response = await fetch(buildApiUrl(`/api/listings/${encodeURIComponent(listingId)}/moderation`, base), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache",
        },
        cache: "no-store",
        body: JSON.stringify(payload),
      });

      const result = await readJson<{ success: boolean; listing: ListingRecord }>(response);
      preferredListingsApiBase = base;
      return result;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Listing moderation request failed.");
    }
  }

  throw lastError || new Error("Listing moderation request failed.");
};

export const updateListingRecord = async (
  token: string,
  listingId: string,
  listing: ListingRecord,
) => {
  const bases = getApiBaseCandidates(preferredListingsApiBase);
  let lastError: Error | null = null;

  for (const base of bases) {
    try {
      const response = await fetch(buildApiUrl(`/api/listings/${encodeURIComponent(listingId)}`, base), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache",
        },
        cache: "no-store",
        body: JSON.stringify({ listing }),
      });

      const result = await readJson<{ success: boolean; listing: ListingRecord }>(response);
      preferredListingsApiBase = base;
      return result;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Listing update request failed.");
    }
  }

  throw lastError || new Error("Listing update request failed.");
};

export const recordListingMetric = async (
  listingId: string,
  payload: {
    eventType: "view" | "action-click";
    actionType?: "buy-now" | "lease-now" | "bnpl";
  },
) => {
  const bases = getApiBaseCandidates(preferredListingsApiBase);
  let lastError: Error | null = null;

  for (const base of bases) {
    try {
      const response = await fetch(buildApiUrl(`/api/listings/${encodeURIComponent(listingId)}/metrics`, base), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
        cache: "no-store",
        body: JSON.stringify(payload),
      });

      const result = await readJson<{
        success: boolean;
        metrics?: {
          views: number;
          totalActionClicks: number;
          buyNowClicks: number;
          leaseNowClicks: number;
          bnplClicks: number;
        };
      }>(response);
      preferredListingsApiBase = base;
      return result.metrics || null;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Listing metrics request failed.");
    }
  }

  throw lastError || new Error("Listing metrics request failed.");
};
